<template>
  <BaseDialog :is-open="isOpen" @close="emit('close')">
    <div class="p-4">
      <template v-if="step === 'initial'">
        <div class="flex-1 overflow-y-auto">
          <section class="mb-2">
            <div
              class="flex items-center justify-center text-lg font-semibold"
            >
              <template
                v-if="transaction.status === 'successful'"
              >
                <span> {{ typeLabel }} Successful </span>
                <IconCheckCircle
                  class="ml-2 text-positive"
                />
              </template>
              <template
                v-else-if="transaction.status === 'failed'"
              >
                <span> {{ typeLabel }} Failed </span>
                <IconXCircle class="ml-2 text-error" />
              </template>
              <template v-else>
                <span
                  >{{ typeLabel }} {{ statusLabel }}</span
                >
                <IconExclamationCircle
                  class="ml-2 text-warning"
                />
              </template>
            </div>
          </section>
          <section>
            <ActionItemSmall
              label="Download Merchant Receipt"
              :icon="IconPrintReceipt"
              @click="onDownloadMerchantReceipt"
            />

            <ActionItemSmall
              label="Email Customer Receipt"
              :icon="IconEmailReceipt"
              @click="step = 'emailReceipt'"
            />
            <ActionItemSmall
              v-if="organizationConfig?.smsServiceEnabled"
              label="SMS Customer Receipt"
              :icon="IconSMSReceipt"
              @click="step = 'smsReceipt'"
            />
          </section>
        </div>
      </template>

      <div v-else-if="step === 'emailReceipt'">
        <DialogButtonClose
          class="mb-4"
          @click="step = 'initial'"
        />
        <ReceiptEmailForm
          :object-id="transaction.id"
          receipt-type="transaction"
          @submit-success="emit('close')"
        />
      </div>

      <div
        v-else-if="step === 'smsReceipt'"
        class="px-3 pb-4"
      >
        <DialogButtonClose
          class="mb-4"
          @click="step = 'initial'"
        />
        <ReceiptSMSForm
          :object-id="transaction.id"
          receipt-type="transaction"
          @submit-success="emit('close')"
        />
      </div>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import {
  IconEmailReceipt,
  IconSMSReceipt,
  IconPrintReceipt,
} from "@/icons"

import { pascalize } from "humps"
import type {
  OrderDetailPayment,
  OrderDetailRefund,
} from "@/services/orders/retrieveOrder"

const props = defineProps<{
  transaction: OrderDetailRefund | OrderDetailPayment
  isOpen: boolean
}>()

const { organizationConfig } = useOrganizationConfig()

watch(
  () => props.isOpen,
  (value) => {
    if (value === true) {
      step.value = "initial"
    }
  }
)

const emit = defineEmits(["close"])

const step = ref<
  "initial" | "refund" | "emailReceipt" | "smsReceipt"
>("initial")

watch(
  () => props.isOpen,
  (newValue, oldValue) => {
    if (newValue && !oldValue) {
      step.value = "initial"
    }
  }
)

const typeLabel = computed(
  () =>
    (
      ({
        payment: "Payment",
        refund: "Refund",
        reversal: "Reversal",
      }) as const
    )[props.transaction.type]
)

const statusLabel = computed(() =>
  pascalize(props.transaction.status)
)

const onDownloadMerchantReceipt = async () => {
  if (!props.transaction) return
  const { receipt } = await retrieveTransactionReceipt(
    props.transaction.id,
    { isMerchant: true }
  )
  download(
    receipt,
    `Transaction: ${props.transaction.id} receipt (merchant).txt`,
    "text/plain"
  )
}
</script>
